// ClientInvoicePay — PAYING PENDING INVOICES IS A PAGE (2026-08-14), not a
// modal. Card entry, a saved-card row, Apple Pay, the wallet split and the
// bank-transfer alternative do not fit in a 440px box, and squeezing them in
// produced a modal too narrow and too long to read.
//
// THE RAILS ARE THE WALLET TOP-UP'S RAILS. Every payment mechanism here is the
// one already proven on Add Funds (2026-08-14):
//   · new card   → HostedCardPanel  (embedded N-Genius fields + Apple Pay)
//   · saved card → SavedCardPicker + SavedCardPay (CVV only, 3DS relay)
//   · wallet     → the same POST, no card leg
// The server endpoint is unchanged too — POST /client-requests/invoices/
// pay-pending owns the fresh total, the drift guard, the wallet clamp and the
// tier test for all of them.
//
// TWO STEPS: review the figures, then pay. The card form only ever appears on
// step 2, so step 1 stays the short, readable summary it always was.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import PayNowPanel from "../PayNowPanel";
import HostedCardPanel from "../payments/HostedCardPanel";
import SavedCardPay from "../payments/SavedCardPay";
import SavedCardPicker from "../ClientWallet/SavedCardPicker";
import SaveCardNamer from "../payments/SaveCardNamer";
import ReceiptDownload from "../payments/ReceiptDownload";
import { CARD_CONSENT_TEXT } from "../ClientWallet/cardConsent";
// The bank-transfer flow, rendered INLINE as one of this page's methods
// (same component, `inline` mode) — never a modal on top of the page the
// client is already paying on.
import PendingInvoicePaymentForm from "./PendingInvoicePaymentModal";
import ClientDepositForm from "../ClientWallet/ClientDepositForm";
import { resolvePayNow, payNowBody } from "../payNow";
import { availableBalance } from "../walletFigures";
import {
    isPaymentInReview,
    paymentInReviewMessage,
    paymentErrorMessage,
} from "../paymentErrors";
import "../ClientWallet/savedCards.css";
import "./clientInvoicePay.css";

const money = (n) => `AED ${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// The card ceiling for ONE gateway capture (server: BANK_TRANSFER_TIER_AED).
// This only decides whether the UI offers the card rail, never what is charged.
const CARD_TIER_LIMIT = 10000;

// THE WALLET CEILING FOR THIS RAIL — bonus credit may not pay a Zoho invoice,
// so only cash counts: min(available, cash) is bit-for-bit what the server
// clamps wallet_portion to. Due adjustments never reduce it.
const invoiceWalletCeiling = (wallet) => {
    if (!wallet) return 0;
    const available = availableBalance(wallet);
    const cash = Number(wallet.cash_balance);
    const ceiling = Number.isFinite(cash) ? Math.min(available, cash) : available;
    return Math.max(0, Math.round(ceiling * 100) / 100);
};

export default function ClientInvoicePay() {
    const navigate = useNavigate();
    // Separate entity wallets: /client/invoices/pay?wallet=<id> pays THAT
    // wallet's own pending pool from that wallet; without it, the main one.
    const [searchParams] = useSearchParams();
    const walletId = parseInt(searchParams.get("wallet"), 10) || null;
    const walletQuery = walletId ? { wallet: walletId } : {};
    const [walletLabel, setWalletLabel] = useState(null);

    const [breakdown, setBreakdown] = useState(null);
    const [loading, setLoading] = useState(true);
    const [wallet, setWallet] = useState(null);
    const [walletError, setWalletError] = useState(false);
    const [useWallet, setUseWallet] = useState(true);
    const [savedCards, setSavedCards] = useState([]);
    const [cardSelection, setCardSelection] = useState("new");
    const [savedCvv, setSavedCvv] = useState("");
    const [savedCardBusy, setSavedCardBusy] = useState(false);
    const [saveNewCard, setSaveNewCard] = useState(false);
    // 'probe' renders the embedded panel; it downgrades itself to 'off' when the
    // rail is disabled or the SDK cannot mount, and the page falls back to the
    // redirect paypage. Never a broken embed.
    const [hostedRail, setHostedRail] = useState("probe");

    const [step, setStep] = useState("review"); // review | card
    const [paying, setPaying] = useState(false);
    const [error, setError] = useState("");
    const [info, setInfo] = useState("");
    const [paid, setPaid] = useState(null); // { amount }
    // The Zoho receipt for the payment just made, offered on the success screen
    // (2026-08-14) — resolved from the settlement, which is applied a moment
    // AFTER the checkout reads paid, so it arrives by polling rather than at once.
    const receiptHandleRef = useRef(null); // { order_ref } | { settlement_id }
    // The card just stored, offered for naming on the confirmation screen.
    const [justSavedCard, setJustSavedCard] = useState(null);
    // WHICH RAIL, asked first and answered at the top of the page — the Add
    // Funds pattern. 'card' covers card, Apple Pay and the wallet (the split
    // is arithmetic, never a third button); 'bank' is the transfer flow.
    const [method, setMethod] = useState("card");
    const [topUpOpen, setTopUpOpen] = useState(false);

    const loadBreakdown = useCallback(async () => {
        try {
            const res = await axios.get("/api/client-requests/invoices/pending-breakdown", { params: walletQuery });
            setBreakdown(res.data || null);
            return res.data || null;
        } catch (e) {
            setError(e.response?.data?.message || "Could not load your pending amount. Please try again.");
            return null;
        }
    }, [walletId]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadWallet = useCallback(() => {
        setWalletError(false);
        return axios.get("/api/client-requests/wallet", { params: walletQuery })
            .then((r) => {
                setWallet(r.data || null);
                setWalletLabel(r.data?.wallet_label || null);
                // ON by default only when there is money this rail can spend.
                setUseWallet(invoiceWalletCeiling(r.data) > 0.005);
            })
            .catch(() => { setWallet(null); setWalletError(true); });
    }, [walletId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        let cancelled = false;
        (async () => {
            await Promise.all([loadBreakdown(), loadWallet()]);
            if (!cancelled) setLoading(false);
        })();
        // Saved cards are a convenience — a failure just means the new-card form.
        axios.get("/api/cards")
            .then((r) => {
                if (cancelled) return;
                const list = Array.isArray(r?.data?.cards) ? r.data.cards : [];
                setSavedCards(list);
                if (list.length > 0) setCardSelection(list[0].id);
            })
            .catch(() => { /* new card only */ });
        return () => { cancelled = true; };
    }, [loadBreakdown, loadWallet]);

    const total = Number(breakdown?.total || 0);
    const walletCeiling = walletError ? 0 : invoiceWalletCeiling(wallet);
    const plan = useMemo(
        () => resolvePayNow({ amount: total, spendable: walletCeiling, useWallet: useWallet && !walletError }),
        [total, walletCeiling, useWallet, walletError]
    );
    // The tier applies to the CARD LEG — wallet money never reaches the gateway,
    // so a 12,000 debt with 3,000 from the wallet is a payable 9,000 card charge.
    const cardOverTier = plan.needsCard && plan.cardPortion > CARD_TIER_LIMIT;
    // Until the balance lands the plan is card-only by default; paying now would
    // silently skip a split the client is entitled to.
    const walletPending = !walletError && wallet === null;
    const selectedSavedCard = savedCards.find((c) => c.id === cardSelection) || null;
    const savedCardActive = typeof cardSelection === "number" && !!selectedSavedCard;
    const embeddedCardActive = hostedRail !== "off" && !savedCardActive;

    // The figure the client confirmed. Sent as expected_total so the server's
    // PENDING_DRIFT guard can refuse a total that moved under us.
    const expectedTotal = useCallback(() => {
        const n = Number(breakdown?.total);
        return Number.isFinite(n) ? n : null;
    }, [breakdown]);

    const handleDrift = useCallback(async (e) => {
        if (e.response?.data?.code !== "PENDING_DRIFT") return false;
        const fresh = e.response.data.breakdown;
        if (fresh && fresh.total != null) setBreakdown(fresh);
        else await loadBreakdown();
        setStep("review");
        const t = Number(fresh?.total ?? NaN);
        setError(
            Number.isFinite(t)
                ? `Your outstanding total changed to ${money(t)} — review and confirm.`
                : "Your outstanding total changed — review the updated amount and confirm."
        );
        return true;
    }, [loadBreakdown]);

    // Everything the page needs to re-read once money has moved.
    const refreshAfterPayment = useCallback(async () => {
        await Promise.all([loadBreakdown(), loadWallet()]);
    }, [loadBreakdown, loadWallet]);

    // THE RECEIPT LINK — the server resolves the settlement's Zoho payment
    // from this handle and streams the PDF in one call. No browser-side lookup.
    const receiptHref = useCallback(() => {
        const h = receiptHandleRef.current;
        // No handle, no link — never a URL the server must reject.
        if (!h || (!h.order_ref && h.settlement_id == null)) return null;
        const q = new URLSearchParams(h);
        return `/api/client-requests/invoices/settlement-receipt-pdf?${q.toString()}`;
    }, []);

    const body = useCallback((extra = {}) => {
        const b = payNowBody(plan, extra);
        const exp = expectedTotal();
        if (exp != null) b.expected_total = exp;
        if (walletId) b.wallet_client_id = walletId;
        return b;
    }, [plan, expectedTotal, walletId]);

    // --- WALLET-ONLY: no card leg, one POST. -------------------------------
    const payFromWallet = useCallback(async () => {
        setPaying(true);
        setError("");
        setInfo("");
        try {
            const res = await axios.post("/api/client-requests/invoices/pay-pending", body());
            if (res.data?.nothing_due) {
                setPaid({ amount: 0, nothingDue: true });
            } else if (res.data?.ok && res.data?.rail === "wallet") {
                setPaid({ amount: Number(res.data.settled_total || total) });
                // Wallet rail: no gateway order, so pay-pending hands back the
                // settlement row id for exactly this payment.
                receiptHandleRef.current = res.data.settlement_id != null
                    ? { settlement_id: res.data.settlement_id }
                    : null;
                    } else if (res.data?.payment_url) {
                // The server routed this to a paypage (embedded rail unavailable).
                window.location.assign(res.data.payment_url);
                return;
            } else {
                setError("Could not complete the payment. Please try again.");
            }
            await refreshAfterPayment();
        } catch (e) {
            if (!(await handleDrift(e))) {
                if (isPaymentInReview(e)) setInfo(paymentInReviewMessage(e));
                else setError(paymentErrorMessage(e, "Could not complete the payment. Please try again."));
            }
        } finally {
            setPaying(false);
        }
    }, [body, total, refreshAfterPayment, handleDrift]);

    // --- NEW CARD (embedded): the SDK's session completes the payment. -----
    const startHostedPayment = useCallback(async (sessionId) => {
        setError("");
        setInfo("");
        try {
            const res = await axios.post("/api/client-requests/invoices/pay-pending", body({
                session_id: sessionId,
                ...(saveNewCard ? { save_card: true, save_card_consent: true } : {}),
            }));
            // The card leg vanished between mounting the form and pressing Pay
            // (the wallet grew, the invoices were settled elsewhere): the server
            // took no card money. Say so — never hand the SDK an undefined
            // payment to authenticate.
            if (!res.data?.payment_response) {
                if (res.data?.nothing_due) setPaid({ amount: 0, nothingDue: true });
                else if (res.data?.rail === "wallet") setPaid({ amount: Number(res.data.settled_total || total), fromWallet: true });
                else { setError("Could not start the card payment. Please try again."); return null; }
                await refreshAfterPayment();
                return null;
            }
            receiptHandleRef.current = res.data.order_ref ? { order_ref: res.data.order_ref } : null;
            return res.data;
        } catch (e) {
            if (!(await handleDrift(e))) {
                if (isPaymentInReview(e)) setInfo(paymentInReviewMessage(e));
                else setError(paymentErrorMessage(e, "Could not start the card payment. Please try again."));
            }
            return null;
        }
    }, [body, saveNewCard, total, refreshAfterPayment, handleDrift]);

    // --- SAVED CARD: the same POST plus the card id and its code. ----------
    const chargeSavedCard = useCallback(async (cvv) => {
        const res = await axios.post("/api/client-requests/invoices/pay-pending", body({
            payment_method_id: cardSelection,
            cvv,
        }));
        receiptHandleRef.current = res.data?.order_ref ? { order_ref: res.data.order_ref } : null;
        return res.data;
    }, [body, cardSelection]);

    const onPaidVerified = useCallback(async () => {
        setPaid({ amount: total });
        setStep("review");
        // If they asked us to keep the card, fetch it so it can be NAMED right
        // here — the same offer the top-up success makes. Naming happens at the
        // moment of saving or not at all (2026-08-14). Newest first, so the
        // card just stored is list[0]. Best-effort: no card, no offer.
        if (saveNewCard) {
            try {
                const c = await axios.get("/api/cards");
                const list = Array.isArray(c.data?.cards) ? c.data.cards : [];
                if (list[0]) setJustSavedCard(list[0]);
            } catch { /* naming simply isn't offered */ }
        }
        await refreshAfterPayment();
    }, [total, refreshAfterPayment, saveNewCard]);

    const backToInvoices = () => navigate("/client/invoices");

    // ---------------------------------------------------------------- render
    // ONE shell for every state (2026-08-14): the back link and the title
    // are always there, and loading is the portal's own app-spinner inside the
    // same panel the content will fill — not a bare sentence floating on the
    // page while everything else is missing.
    const shell = (title, inner) => (
        <div className="cipay">
            <div className="cipay__shell">
                <button type="button" className="cipay__back" onClick={backToInvoices}>
                    ← Invoices
                </button>
                <h1 className="cipay__title">{walletId && walletLabel ? `${title} — ${walletLabel}` : title}</h1>
                {walletId && walletLabel && (
                    <p className="cipay__walletNote">These are the {walletLabel} entity's invoices — paid from its own wallet.</p>
                )}
                {inner}
            </div>

            {/* "Or top up your wallet and earn bonuses" — the SAME Add Funds
                form the wallet page opens, in place. */}
            {/* A TOP-UP CAN SETTLE THE VERY DEBT THIS PAGE IS ABOUT (the owner
                2026-08-14). Every card top-up captures the client's pending
                invoices in the SAME payment — mandatory since 2026-08-06 — so
                when this modal closes the outstanding figure behind it may
                already be zero. Re-read BOTH on close, not just the wallet, and
                on every exit path (success, card-paid, plain close): a page that
                keeps showing a settled debt invites a second payment for it.

                The server refuses that second payment regardless — pay-pending
                recomputes the total and returns nothing_due before staging
                anything — but the client should never be looking at a figure
                that no longer exists. */}
            {topUpOpen && (
                <ClientDepositForm
                    walletClientId={walletId}
                    walletLabel={walletLabel}
                    onClose={() => { setTopUpOpen(false); refreshAfterPayment(); }}
                    onSuccess={() => { setTopUpOpen(false); refreshAfterPayment(); }}
                />
            )}
        </div>
    );

    if (loading) {
        return shell("Pay pending invoices", (
            <div className="cipay__panel cipay__panel--loading">
                <div className="app-loading">
                    <div className="app-spinner" />
                    <div className="app-spinner-text">Loading your pending amount…</div>
                </div>
            </div>
        ));
    }

    if (paid) {
        return shell("Pay pending invoices", (
            <div className="cipay__done">
                <div className="cipay__doneMark" aria-hidden="true">✓</div>
                <div className="cipay__doneTitle">
                    {paid.nothingDue ? "Nothing was due" : "Payment complete"}
                </div>
                {/* The AMOUNT is the thing a client checks on a confirmation —
                    it leads, the way it does on the top-up success screen,
                    instead of being buried in a sentence. */}
                {!paid.nothingDue && <div className="cipay__doneAmount">{money(paid.amount)}</div>}
                <div className="cipay__doneSub">
                    {paid.nothingDue
                        ? "Your invoices are already settled."
                        : paid.fromWallet
                            ? "Paid from your wallet balance. Your invoices are settled."
                            : "Your invoices are settled."}
                </div>

                {justSavedCard && (
                    <div className="cipay__doneNamer">
                        <SaveCardNamer card={justSavedCard} />
                    </div>
                )}

                <div className="cipay__doneActions">
                    <button type="button" className="cipay__btn cipay__btn--primary" onClick={backToInvoices}>
                        Back to invoices
                    </button>
                    {/* THE RECEIPT, HERE (2026-08-14) — the moment a client
                        wants it is the moment they paid, not after hunting
                        through the payments tab.

                        The button is HERE THE MOMENT THE PAYMENT
                        COMPLETES — no spinner gate, nothing polled. It resolves
                        the payment id when clicked (2026-08-14). */}
                    {!paid.nothingDue && (
                        <ReceiptDownload href={receiptHref()} />
                    )}
                </div>
            </div>
        ));
    }

    // THE BREAKDOWN DID NOT LOAD. Never fall through to "nothing due" on a
    // failed fetch: `total` derives from breakdown, so a timed-out Zoho read
    // used to render "You have no pending invoices 🎉" over a real debt — the
    // page telling a client their invoices are settled when the server never
    // answered (caught live 2026-08-14). Say what happened and offer a retry.
    if (!breakdown) {
        return shell("Pay pending invoices", (
            <div className="cipay__panel">
                <div className="cipay__error">
                    {error || "Could not load your pending amount. Please try again."}
                </div>
                <div className="cipay__actions">
                    <button
                        type="button"
                        className="cipay__btn cipay__btn--primary"
                        onClick={() => { setError(""); setLoading(true); loadBreakdown().finally(() => setLoading(false)); }}
                    >
                        Try again
                    </button>
                    <button type="button" className="cipay__btn cipay__btn--ghost" onClick={backToInvoices}>
                        Back to invoices
                    </button>
                </div>
            </div>
        ));
    }

    if (total <= 0.009) {
        return shell("Pay pending invoices", (
            <div className="cipay__panel">
                <div className="cipay__empty">You have no pending invoices. 🎉</div>
                <button type="button" className="cipay__btn cipay__btn--primary" onClick={backToInvoices}>
                    Back to invoices
                </button>
            </div>
        ));
    }

    // STEP 2 — card entry. Reached ONLY from "use a new card": a saved card is
    // paid on step 1, in its own row, exactly as it is on Add Funds.
    if (step === "card") {
        return shell("Card payment", (
            <div className="cipay__panel">
                <div className="cipay__cardAmount">
                    <span>To pay by card</span>
                    <strong>{money(plan.cardPortion)}</strong>
                </div>
                {plan.method === "wallet_card" && (
                    <div className="cipay__note">
                        The other {money(plan.walletPortion)} of your {money(total)} total comes from your wallet.
                    </div>
                )}

                {info && <div className="cipay__info">{info}</div>}
                {error && <div className="cipay__error">{error}</div>}

                <HostedCardPanel
                    /* Keyed on the card leg: the SDK reads the amount once, at
                       mount, and Apple draws its sheet from that figure — a
                       changed split must build a NEW form, never re-label an
                       old one. */
                    key={`inv-${plan.cardPortion.toFixed(2)}`}
                    payLabel={`Pay ${money(plan.cardPortion)}`}
                    amountAed={plan.cardPortion}
                    disabled={paying}
                    startPayment={startHostedPayment}
                    onUnavailable={() => { setHostedRail("off"); setStep("review"); }}
                    onPaid={onPaidVerified}
                    onFailed={() => { /* the panel states its own outcome */ }}
                >
                    <label className="hcard-save">
                        <input
                            type="checkbox"
                            checked={saveNewCard}
                            onChange={(e) => setSaveNewCard(e.target.checked)}
                        />
                        <span>Save this card for future payments</span>
                    </label>
                    {saveNewCard && (
                        <div className="hcard-consent hcard-consent--readonly">
                            <span className="hcard-consent-text">
                                <span className="hcard-consent-mandate">{CARD_CONSENT_TEXT}</span>
                            </span>
                        </div>
                    )}
                </HostedCardPanel>

                <button
                    type="button"
                    className="cipay__btn cipay__btn--ghost"
                    onClick={() => { setStep("review"); setError(""); }}
                >
                    Back
                </button>
            </div>
        ));
    }

    // STEP 1 — HOW would you like to pay, answered at the TOP (the Add Funds
    // pattern), and then everything that method needs on this same screen: the
    // figures and the wallet toggle, the saved cards, "use a new card or Apple
    // Pay" — or, for bank transfer, the transfer details and proof upload
    // inline (2026-08-14: not a separate modal on top of this page).
    return shell("Pay pending invoices", (
        <div className="cipay__panel">
            {/* Label + tiles are ONE group so the panel's gap spaces the group,
                not the label away from its own choices. */}
            <div className="cipay__methodGroup">
            <div className="cipay__methodLabel">How would you like to pay?</div>
            <div className="cipay__methods">
                <button
                    type="button"
                    className={`cipay__method ${method === "card" ? "is-active" : ""}`}
                    onClick={() => setMethod("card")}
                    disabled={paying || savedCardBusy}
                >
                    <span className="cipay__methodIcon" aria-hidden="true">💳</span>
                    <span className="cipay__methodName">Card or Wallet</span>
                    <span className="cipay__methodSub">Pay now — card, Apple Pay or your balance</span>
                </button>
                <button
                    type="button"
                    className={`cipay__method ${method === "bank" ? "is-active" : ""}`}
                    onClick={() => setMethod("bank")}
                    disabled={paying || savedCardBusy}
                >
                    <span className="cipay__methodIcon" aria-hidden="true">🏦</span>
                    <span className="cipay__methodName">Bank transfer</span>
                    {/* Quotes what would ACTUALLY be transferred under the
                        current wallet toggle, not the gross debt — and says so
                        plainly when the wallet leaves nothing to transfer. */}
                    <span className="cipay__methodSub">
                        {plan.needsCard
                            ? `Transfer ${money(plan.cardPortion)} and upload the proof`
                            : "Covered by your wallet — nothing to transfer"}
                    </span>
                </button>
            </div>
            </div>

            {info && <div className="cipay__info">{info}</div>}
            {error && <div className="cipay__error">{error}</div>}

            {breakdown?.pending_settlement && (
                <div className="cipay__note cipay__note--warn">
                    A bank transfer for your pending invoices is already awaiting our confirmation.
                    Paying now could settle the same invoices twice.
                </div>
            )}

            {breakdown?.pending_settlement && (
                <div className="cipay__note cipay__note--warn">
                    A bank transfer for your pending invoices is already awaiting our confirmation.
                    Paying now could settle the same invoices twice.
                </div>
            )}

            {/* THE WALLET TOGGLE SERVES BOTH RAILS (2026-08-14). Wallet cash
                can fund part of a bank transfer exactly as it funds part of a
                card payment: the client transfers the remainder, and on approval
                the wallet is debited for its share while the invoices settle in
                full. Same one decision, same arithmetic, either method — only
                the wording of the "what is left to pay" row differs. */}
            {walletError ? (
                <div className="cipay__note">
                    Wallet balance unavailable —{" "}
                    <button type="button" className="cipay__linkBtn" onClick={loadWallet}>retry</button>
                </div>
            ) : (
                <PayNowPanel
                    amount={total}
                    wallet={wallet}
                    spendable={walletCeiling}
                    useWallet={useWallet}
                    onUseWallet={setUseWallet}
                    onTopUp={() => setTopUpOpen(true)}
                    label="Outstanding invoices"
                    /* ORDER (2026-08-14): payment cards → wallet toggle →
                       billing-profile summary → figures. The toggle stays
                       directly under the method cards; the summary sits with the
                       numbers it breaks down.

                       The summary renders whenever the server sends
                       entity_breakdown — the rule the modal used.
                       entity_breakdown is null for clients with no per-profile
                       split, and that null IS the condition. Do not add a count
                       check: a single profile still shows. */
                    beforeRows={
                        Array.isArray(breakdown?.entity_breakdown) && breakdown.entity_breakdown.length > 0 && (
                            <div className="cipay__box">
                                <div className="cipay__row">
                                    <span>Outstanding invoices</span>
                                    <span>{money(breakdown.zoho_total)}</span>
                                </div>
                                {breakdown.entity_breakdown.map((e, i) => (
                                    <div key={i} className="cipay__row cipay__row--entity">
                                        <span>{e.label}</span><span>{money(e.amount)}</span>
                                    </div>
                                ))}
                                <div className="cipay__row cipay__row--total">
                                    <span>Total</span><span>{money(total)}</span>
                                </div>
                            </div>
                        )
                    }
                    toggleFirst
                    /* NO entityBreakdown here — the split renders in the summary
                       above; repeating it in the panel read as duplicate info. */
                    dueLabel={method === "bank" ? "To transfer" : undefined}
                    coveredLabel={method === "bank" ? "Nothing to transfer — covered by your wallet" : undefined}
                    disabled={paying || savedCardBusy}
                />
            )}

            {method === "bank" ? (
                walletPending ? (
                    <div className="cipay__note">Checking your wallet balance…</div>
                ) : !plan.needsCard ? (
                    /* The wallet covers the whole debt, so there is nothing to
                       transfer — and a zero-amount transfer is not a payment.
                       The server refuses it too (WALLET_COVERS_TOTAL). */
                    <>
                        <div className="cipay__note">
                            Your wallet covers the whole outstanding amount — there is nothing left to transfer.
                        </div>
                        <button
                            type="button"
                            className="cipay__btn cipay__btn--primary"
                            disabled={paying}
                            onClick={payFromWallet}
                        >
                            {paying ? "Paying…" : `Pay ${money(total)} from wallet`}
                        </button>
                    </>
                ) : (
                    <PendingInvoicePaymentForm
                        inline
                        walletClientId={walletId}
                        /* What they must actually TRANSFER: the debt less
                           whatever the wallet is funding. */
                        total={plan.cardPortion}
                        walletPortion={plan.walletPortion}
                        debtTotal={total}
                        entityBreakdown={breakdown?.entity_breakdown || null}
                        onClose={() => setMethod("card")}
                        onSuccess={() => {
                            setInfo("Transfer submitted — it will be applied to your invoices once our team confirms it.");
                            loadBreakdown();
                        }}
                    />
                )
            ) : (
                <>
                    {cardOverTier ? (
                        <>
                            <div className="cipay__note cipay__note--warn">
                                {plan.walletPortion > 0.005
                                    ? <>Even using {money(plan.walletPortion)} from your wallet, {money(plan.cardPortion)} would
                                        go on the card — over {money(CARD_TIER_LIMIT)} is settled by bank transfer.</>
                                    : <>Amounts over {money(CARD_TIER_LIMIT)} are settled by bank transfer.</>}
                            </div>
                            <button
                                type="button"
                                className="cipay__btn cipay__btn--primary"
                                onClick={() => setMethod("bank")}
                            >
                                Pay by bank transfer
                            </button>
                        </>
                    ) : !plan.needsCard ? (
                        /* The wallet covers it all — there is no card leg to
                           choose a card for. */
                        <button
                            type="button"
                            className="cipay__btn cipay__btn--primary"
                            disabled={paying || walletPending}
                            onClick={payFromWallet}
                        >
                            {paying ? "Paying…" : walletPending ? "Checking wallet…" : `Pay ${money(total)} from wallet`}
                        </button>
                    ) : (
                        <>
                            {/* THE CARD CHOICE IS HERE, not a step later: saved
                                cards first, "use a new card or Apple Pay" always
                                one tap away — the Add Funds picker, verbatim. */}
                            {savedCards.length > 0 && (
                                <div>
                                    <SavedCardPicker
                                        cards={savedCards}
                                        value={cardSelection}
                                        onChange={setCardSelection}
                                        allowSaveNew={false}
                                        disabled={paying || savedCardBusy}
                                        cvvValue={savedCardActive ? savedCvv : null}
                                        onCvvChange={savedCardActive ? setSavedCvv : null}
                                        newCardSub={hostedRail !== "off"
                                            ? "Continue to input card details or select digital payments."
                                            : null}
                                    />
                                </div>
                            )}

                            {savedCardActive ? (
                                <SavedCardPay
                                    card={selectedSavedCard}
                                    compact
                                    cvv={savedCvv}
                                    onCvvChange={setSavedCvv}
                                    onBusyChange={setSavedCardBusy}
                                    payLabel={`Pay ${money(plan.cardPortion)}`}
                                    disabled={paying || walletPending}
                                    charge={chargeSavedCard}
                                    onPaid={onPaidVerified}
                                    onFailed={() => { /* the row states its own outcome */ }}
                                />
                            ) : (
                                <button
                                    type="button"
                                    className="cipay__btn cipay__btn--primary"
                                    disabled={paying || walletPending}
                                    onClick={() => (embeddedCardActive ? setStep("card") : payFromWallet())}
                                >
                                    {paying
                                        ? "Opening secure payment…"
                                        : walletPending
                                            ? "Checking wallet…"
                                            : embeddedCardActive
                                                ? `Continue to card details — ${money(plan.cardPortion)}`
                                                : `Pay ${money(plan.cardPortion)} by card`}
                                </button>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    ));
}
